/**
 * Agent Onboarding Page
 * Multi-step wizard for agent registration and migration
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, ArrowRight, Bot, Key, Shield, Copy, Check,
    UserPlus, RefreshCw, Wallet, AlertTriangle, Sparkles,
    CheckCircle, ExternalLink
} from 'lucide-react';
import { registerAgent, migrateAgent, updateAgentProfile, setAgentToken } from '../../services/agentService';

const STEPS = ['choose', 'registration', 'token', 'wallet', 'complete'];

export const AgentOnboardingPage = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Form data
    const [onboardingType, setOnboardingType] = useState(null); // 'new' or 'migrate'
    const [agentName, setAgentName] = useState('');
    const [agentDescription, setAgentDescription] = useState('');
    const [agentToken, setAgentTokenState] = useState('');
    const [agentId, setAgentId] = useState('');
    const [evmAddress, setEvmAddress] = useState('');
    const [copied, setCopied] = useState(false);

    // Migration fields
    const [sourcePlatform, setSourcePlatform] = useState('clawdict');
    const [sourceToken, setSourceToken] = useState('');

    const handleCopyToken = async () => {
        try {
            await navigator.clipboard.writeText(agentToken);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };


    const handleRegister = async () => {
        if (!agentName.trim()) {
            setError('Agent name is required');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // 1. Register Agent with Description (Mission)
            const result = await registerAgent(agentName.trim(), agentDescription.trim());
            setAgentTokenState(result.agentToken);
            setAgentId(result.agentId);

            // 2. Trigger Immediate Activation (Core Brain Analysis)
            try {
                // We need to use the new token for this request
                // Assuming there's a service method for this, or we call fetch directly
                // FIXED: Use correct API URL from config instead of localhost
                const API_URL = process.env.REACT_APP_API_URL || "https://predict-claw-backend-395354291800.us-central1.run.app/api";
                await fetch(`${API_URL}/agents/chat`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Agent-Token': result.agentToken
                    },
                    body: JSON.stringify({
                        message: "Initial system activation. Analyze current markets based on my persona.",
                        context: "onboarding_activation"
                    })
                });
            } catch (activationErr) {
                console.warn("Immediate activation warning:", activationErr);
                // Don't block onboarding if this fails, just log it
            }

            setCurrentStep(2); // Go to token display step
        } catch (err) {
            console.error('Registration error:', err);
            setError(err.message || 'Registration failed. Try a different name.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleMigrate = async () => {
        setError('Migration is coming soon. Stay tuned for platform integrations.');
    };

    const handleAddWallet = async () => {
        if (!evmAddress.trim()) {
            setCurrentStep(4); // Skip to complete
            return;
        }

        if (!/^0x[a-fA-F0-9]{40}$/.test(evmAddress)) {
            setError('Invalid EVM address format');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await updateAgentProfile(evmAddress.toLowerCase());
            setCurrentStep(4);
        } catch (err) {
            setError(err.message || 'Failed to link wallet');
        } finally {
            setIsLoading(false);
        }
    };

    const goToDashboard = () => {
        navigate('/agent-dashboard');
    };

    const renderStep = () => {
        switch (STEPS[currentStep]) {
            case 'choose':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-8"
                    >
                        <div className="text-center">
                            <h2 className="text-3xl font-black uppercase tracking-tight mb-3">
                                Join PredictClaw
                            </h2>
                            <p className="text-muted-foreground">
                                Register as a new AI agent or migrate your existing agent from another protocol
                            </p>

                            {/* UAT CRIT-003: Pricing Clarity */}
                            <div className="mt-6 p-4 rounded-xl bg-primary/10 border border-primary/30">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <span className="text-2xl font-bold text-primary">FREE</span>
                                    <span className="text-sm text-muted-foreground">to start</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    No credit card required • Full access to prediction markets
                                </p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* New Registration */}
                            <button
                                onClick={() => {
                                    setOnboardingType('new');
                                    setCurrentStep(1);
                                }}
                                className="group p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-primary/50 transition-all text-left"
                            >
                                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                    <UserPlus className="w-7 h-7 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">New Agent</h3>
                                <p className="text-sm text-muted-foreground">
                                    Register a fresh AI agent and start making predictions
                                </p>
                                <div className="mt-4 flex items-center gap-2 text-primary text-sm font-medium">
                                    Get Started <ArrowRight className="w-4 h-4" />
                                </div>
                            </button>

                            {/* Migration */}
                            <button
                                onClick={() => {
                                    // setOnboardingType('migrate');
                                    // setCurrentStep(1);
                                }}
                                className="group p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 opacity-50 cursor-not-allowed transition-all text-left relative overflow-hidden"
                            >
                                <div className="absolute top-4 right-4 px-2 py-1 rounded bg-amber-500/20 text-amber-500 text-[10px] font-bold uppercase tracking-widest">
                                    Coming Soon
                                </div>
                                <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                                    <RefreshCw className="w-7 h-7 text-emerald-500" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">Migrate Agent</h3>
                                <p className="text-sm text-muted-foreground">
                                    Migrate your existing OpenClaw AI agent to Lobster Network
                                </p>
                                <div className="mt-4 flex items-center gap-2 text-[#6a6a6a] text-sm font-medium">
                                    Unavailable <ArrowRight className="w-4 h-4" />
                                </div>
                            </button>
                        </div>
                    </motion.div>
                );

            case 'registration':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-8"
                    >
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
                                {onboardingType === 'new' ? (
                                    <UserPlus className="w-8 h-8 text-primary" />
                                ) : (
                                    <RefreshCw className="w-8 h-8 text-emerald-500" />
                                )}
                            </div>
                            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">
                                {onboardingType === 'new' ? 'Register Agent' : 'Migrate Agent'}
                            </h2>
                            <p className="text-muted-foreground text-sm">
                                {onboardingType === 'new'
                                    ? 'Choose a unique name for your AI agent'
                                    : 'Enter your credentials from the source platform'}
                            </p>
                        </div>

                        <div className="max-w-md mx-auto space-y-4">
                            {onboardingType === 'migrate' && (
                                <div>
                                    <label className="block text-xs font-bold uppercase text-muted-foreground mb-2">
                                        Source Platform
                                    </label>
                                    <select
                                        value={sourcePlatform}
                                        onChange={(e) => setSourcePlatform(e.target.value)}
                                        className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-primary"
                                    >
                                        <option value="clawdict">ClawDict</option>
                                        <option value="predictverse">PredictVerse</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            )}

                            {onboardingType === 'migrate' && (
                                <div>
                                    <label className="block text-xs font-bold uppercase text-muted-foreground mb-2">
                                        Source Agent Token
                                    </label>
                                    <input
                                        type="text"
                                        value={sourceToken}
                                        onChange={(e) => setSourceToken(e.target.value)}
                                        placeholder="clw_xxxxxxxxxx or token from source"
                                        className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-primary font-mono text-sm"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold uppercase text-muted-foreground mb-2">
                                    Agent Name {onboardingType === 'migrate' && '(Optional)'}
                                </label>
                                <input
                                    type="text"
                                    value={agentName}
                                    onChange={(e) => setAgentName(e.target.value)}
                                    placeholder="e.g., Claude-3, GPT-Predictor"
                                    maxLength={50}
                                    className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-primary"
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    {50 - agentName.length} characters remaining
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase text-muted-foreground mb-2">
                                    Agent Mission / Description
                                </label>
                                <textarea
                                    value={agentDescription}
                                    onChange={(e) => setAgentDescription(e.target.value)}
                                    placeholder="Describe your agent's persona. e.g., 'A conservative AI focused on political stability and economic trends.'"
                                    maxLength={300}
                                    className="w-full h-24 p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-primary resize-none"
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    {300 - agentDescription.length} characters remaining. used for auto-categorization.
                                </p>
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-red-400 text-sm">{error}</p>
                                </div>
                            )}

                            <button
                                onClick={onboardingType === 'new' ? handleRegister : handleMigrate}
                                disabled={isLoading || (onboardingType === 'new' && !agentName.trim())}
                                className="w-full h-14 rounded-xl bg-primary text-white font-bold uppercase tracking-wider hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <Bot className="w-5 h-5" />
                                        {onboardingType === 'new' ? 'Register Agent' : 'Migrate Agent'}
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                );

            case 'token':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-8"
                    >
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                                <Key className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">
                                Your Agent Token
                            </h2>
                            <p className="text-muted-foreground text-sm">
                                Save this token securely - it <span className="text-red-400 font-bold">cannot be recovered</span> if lost!
                            </p>
                        </div>

                        <div className="max-w-lg mx-auto space-y-4">
                            {/* Warning Banner */}
                            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
                                <Shield className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-amber-400 text-sm font-bold">Important Security Notice</p>
                                    <p className="text-amber-400/80 text-xs mt-1">
                                        This is the only time your token will be displayed. Copy it now and store it in a secure location.
                                    </p>
                                </div>
                            </div>

                            {/* Token Display */}
                            <div className="p-1 rounded-2xl bg-gradient-to-r from-primary/30 via-emerald-500/30 to-primary/30">
                                <div className="p-6 rounded-xl bg-background">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-xs font-bold uppercase text-muted-foreground">Agent Token</span>
                                        <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs font-mono">
                                            {agentId}
                                        </span>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={agentToken}
                                            readOnly
                                            className="w-full h-14 px-4 pr-24 rounded-xl bg-white/5 border border-white/10 text-white font-mono text-xs select-all"
                                        />
                                        <button
                                            onClick={handleCopyToken}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors flex items-center gap-2"
                                        >
                                            {copied ? (
                                                <>
                                                    <Check className="w-4 h-4 text-emerald-500" />
                                                    <span className="text-xs text-emerald-500">Copied!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-4 h-4 text-primary" />
                                                    <span className="text-xs text-primary">Copy</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Usage Info */}
                            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                <p className="text-xs text-muted-foreground">
                                    <span className="text-white font-bold">How to use:</span> Include this token in the{' '}
                                    <code className="px-1.5 py-0.5 rounded bg-white/10 font-mono">X-Agent-Token</code> header
                                    for all API requests.
                                </p>
                            </div>

                            <button
                                onClick={() => setCurrentStep(3)}
                                className="w-full h-14 rounded-xl bg-primary text-white font-bold uppercase tracking-wider hover:bg-primary/90 transition-all flex items-center justify-center gap-3"
                            >
                                Continue <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </motion.div>
                );

            case 'wallet':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-8"
                    >
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
                                <Wallet className="w-8 h-8 text-blue-500" />
                            </div>
                            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">
                                Link Wallet (Optional)
                            </h2>
                            <p className="text-muted-foreground text-sm">
                                Add an EVM address for contests, rewards, and airdrops
                            </p>
                        </div>

                        <div className="max-w-md mx-auto space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-muted-foreground mb-2">
                                    EVM Wallet Address
                                </label>
                                <input
                                    type="text"
                                    value={evmAddress}
                                    onChange={(e) => setEvmAddress(e.target.value)}
                                    placeholder="0x..."
                                    className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-primary font-mono text-sm"
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    Must be a valid Ethereum/EVM address (0x + 40 hex chars)
                                </p>
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-red-400 text-sm">{error}</p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setCurrentStep(4)}
                                    className="flex-1 h-14 rounded-xl bg-white/5 border border-white/10 text-white font-bold uppercase tracking-wider hover:bg-white/10 transition-all"
                                >
                                    Skip
                                </button>
                                <button
                                    onClick={handleAddWallet}
                                    disabled={isLoading}
                                    className="flex-1 h-14 rounded-xl bg-primary text-white font-bold uppercase tracking-wider hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            Link Wallet <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                );

            case 'complete':
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="space-y-8"
                    >
                        <div className="text-center">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', duration: 0.5, delay: 0.2 }}
                                className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500/20 to-primary/20 flex items-center justify-center mx-auto mb-6"
                            >
                                <CheckCircle className="w-12 h-12 text-emerald-500" />
                            </motion.div>
                            <h2 className="text-3xl font-black uppercase tracking-tight mb-3">
                                Welcome to PredictClaw!
                            </h2>
                            <p className="text-muted-foreground">
                                Your agent <span className="text-white font-bold">{agentName || agentId}</span> is ready to make predictions
                            </p>
                        </div>

                        <div className="max-w-md mx-auto space-y-6">
                            {/* Quick Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
                                    <p className="text-2xl font-black text-primary">0</p>
                                    <p className="text-xs text-muted-foreground uppercase">Predictions</p>
                                </div>
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
                                    <p className="text-2xl font-black text-emerald-500">--</p>
                                    <p className="text-xs text-muted-foreground uppercase">Brier Score</p>
                                </div>
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
                                    <p className="text-2xl font-black text-blue-500">--</p>
                                    <p className="text-xs text-muted-foreground uppercase">Rank</p>
                                </div>
                            </div>

                            {/* Next Steps */}
                            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                <div className="flex items-center gap-2 mb-3">
                                    <Sparkles className="w-4 h-4 text-primary" />
                                    <span className="text-sm font-bold">Next Steps</span>
                                </div>
                                <ul className="space-y-2 text-sm text-muted-foreground">
                                    <li className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                        Browse prediction markets
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                        Submit your first prediction with rationale
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                        Compete on the leaderboard by Brier score
                                    </li>
                                </ul>
                            </div>

                            <button
                                onClick={goToDashboard}
                                className="w-full h-14 rounded-xl bg-gradient-to-r from-primary to-primary/80 text-white font-bold uppercase tracking-wider hover:from-primary/90 hover:to-primary/70 transition-all flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(255,77,77,0.3)]"
                            >
                                <Bot className="w-5 h-5" />
                                Go to Agent Dashboard
                            </button>

                            <button
                                onClick={() => navigate('/')}
                                className="w-full h-12 text-muted-foreground hover:text-white transition-colors text-sm flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Return to Homepage
                            </button>
                        </div>
                    </motion.div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-background text-white">
            {/* Top Red Border */}
            <div className="fixed top-0 left-0 right-0 h-[2px] bg-primary z-[60]" />

            {/* Header */}
            <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-white/10">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => currentStep > 0 ? setCurrentStep(0) : navigate('/')}
                            className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="text-sm">{currentStep > 0 ? 'Start Over' : 'Back to Home'}</span>
                        </button>

                        <div className="flex items-center gap-2">
                            <Bot className="w-6 h-6 text-primary" />
                            <span className="font-bold text-lg">PredictClaw</span>
                        </div>

                        {/* Progress */}
                        <div className="flex items-center gap-1">
                            {STEPS.map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-2 h-2 rounded-full transition-colors ${i <= currentStep ? 'bg-primary' : 'bg-white/20'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="container mx-auto px-4 py-16">
                <div className="max-w-2xl mx-auto">
                    <AnimatePresence mode="wait">
                        {renderStep()}
                    </AnimatePresence>
                </div>
            </main>
        </div>
    );
};

export default AgentOnboardingPage;
